import { toast } from "react-toastify";
import {
  PlatformCatalog,
  type NewTrialStateInterface,
  type StepItemType,
} from "./types";
import { formatTranslation } from "../../shared/utils/translationUtils";
import {
  optimizeStripsDataForBackend,
  transformToBackendFormat,
  type FullStripsData,
} from "../../shared/layout/PlotCard/utils/optimizeStripsData";

export const getItems = (steps: any): Array<{ key: string; title: string }> => {
  return steps.map((item: StepItemType) => ({
    key: item.title,
    title: item.title,
  }));
};

export const getCancelButtonLabel = (current: number, t: any): string => {
  if (current === 0)
    return formatTranslation(t("createProtocol.objectives.cancelButton"));
  return formatTranslation(t("report.trial.back"));
};

export const getNextButtonLabel = (
  current: number,
  t: any,
  formatTranslation: any,
  steps: any
): string => {
  if (!Array.isArray(steps) || steps.length === 0)
    return formatTranslation(t("trials.saveAndShareReport"));

  const nextStep = steps[current + 1];

  if (current >= steps.length - 1 || !nextStep?.title)
    return formatTranslation(t("trials.saveAndShareReport"));
  return nextStep.title;
};

export const getNext = (
  current: number,
  setCurrent: React.Dispatch<React.SetStateAction<number>>
): void => {
  setCurrent(current + 1);
};

export const getPrev = (
  current: number,
  setCurrent: React.Dispatch<React.SetStateAction<number>>
): void => {
  setCurrent(current - 1);
};

export const downloadWorkOrder = (blob: Blob): void => {
  const url = window.URL.createObjectURL(blob);
  const documentLink = document.createElement("a");
  documentLink.href = url;
  documentLink.download = "downloadWorkOrder";
  document.body.appendChild(documentLink);
  documentLink.click();
  documentLink.remove();
  window.URL.revokeObjectURL(url);
};

export const allowFieldInputChange = (
  trialInfo: any,
  newTrialState: NewTrialStateInterface
): boolean => {
  if (
    trialInfo?.ab_line?.length > 0 ||
    trialInfo?.buffer_zone?.size ||
    trialInfo?.trial_plots?.length > 0 ||
    newTrialState?.ab_line?.length > 0 ||
    newTrialState?.buffer_zone?.length > 0 ||
    newTrialState?.trial_plots?.length > 0
  ) {
    return false;
  } else {
    return true;
  }
};

export const convertToCatalogAbbreviation = (
  catalogValue: PlatformCatalog | string
): string => {
  const catalogAbbreviations: { [key in PlatformCatalog]: string } = {
    [PlatformCatalog.Liters]: "L/ha",
    [PlatformCatalog.Kilograms]: "kg/ha",
    [PlatformCatalog.Units]: "pcs/ha",
    [PlatformCatalog.Boxes]: "box/ha",
    [PlatformCatalog.Sack]: "sck/ha",
    [PlatformCatalog.Gallon]: "gal/ha",
    [PlatformCatalog.Grams]: "g/ha",
    [PlatformCatalog.Tonnes]: "t/ha",
    [PlatformCatalog.Milliliter]: "mL/ha",
    [PlatformCatalog.Meters]: "m/ha",
    [PlatformCatalog.Ounces]: "oz/ha",
    [PlatformCatalog.Pounds]: "lbs/ha",
    [PlatformCatalog.Pints]: "pt/ha",
    [PlatformCatalog.Dose]: "doses/ha",
    [PlatformCatalog.Quarts]: "qt/ha",
  };

  return catalogAbbreviations[catalogValue as PlatformCatalog] || "Unknown";
};

export const RATES_TRIAL_ORDER = ["seed", "biological", "nutrient"] as const;

export type RatesTrialType = (typeof RATES_TRIAL_ORDER)[number];

export const RATES_TRIAL_LABELS: Record<RatesTrialType, string> = {
  seed: "Seeds",
  biological: "CP and biologicals",
  nutrient: "Fertiliser",
};

export interface RatesTrialStepState {
  selectedRatesTrialTypes: RatesTrialType[];
  hasSelectedRatesTrialTypes: boolean;
  activeRatesTrialType: RatesTrialType | undefined;
  nextRatesTrialType: RatesTrialType | undefined;
  ratesCurrentTypeValid: boolean;
  ratesAllSelectedTypesValid: boolean;
}

/**
 * Derives the Rates & Dosage step's sub-tab state (Seeds / CP & Biologicals /
 * Fertiliser) from the trial's protocol types and per-type validation flags.
 */
export const getRatesTrialStepState = (
  protocolTypes: string[] | undefined,
  activeRatesTab: string | undefined,
  ratesDosageValidationByType: Record<string, boolean>
): RatesTrialStepState => {
  const selectedRatesTrialTypes = RATES_TRIAL_ORDER.filter((type) =>
    protocolTypes?.includes(type)
  );
  const hasSelectedRatesTrialTypes = selectedRatesTrialTypes.length > 0;

  const activeRatesTrialType = selectedRatesTrialTypes.includes(
    activeRatesTab as RatesTrialType
  )
    ? (activeRatesTab as RatesTrialType)
    : selectedRatesTrialTypes[0];

  const nextRatesTrialType = activeRatesTrialType
    ? selectedRatesTrialTypes[
        selectedRatesTrialTypes.indexOf(activeRatesTrialType) + 1
      ]
    : undefined;

  const ratesCurrentTypeValid = activeRatesTrialType
    ? ratesDosageValidationByType[activeRatesTrialType]
    : false;

  const ratesAllSelectedTypesValid =
    hasSelectedRatesTrialTypes &&
    selectedRatesTrialTypes.every((type) => ratesDosageValidationByType[type]);

  return {
    selectedRatesTrialTypes,
    hasSelectedRatesTrialTypes,
    activeRatesTrialType,
    nextRatesTrialType,
    ratesCurrentTypeValid,
    ratesAllSelectedTypesValid,
  };
};

/**
 * The "Next" button on the Rates & Dosage step must stay disabled until the
 * active sub-tab (or, on the last sub-tab, all sub-tabs) is valid. Every other
 * step falls back to the form's own checkValue.
 */
export const getEffectiveCheckValueForStep = (
  current: number,
  checkValue: boolean,
  ratesTrialStepState: RatesTrialStepState
): boolean => {
  if (current !== 2) return checkValue;

  const {
    hasSelectedRatesTrialTypes,
    nextRatesTrialType,
    ratesCurrentTypeValid,
    ratesAllSelectedTypesValid,
  } = ratesTrialStepState;

  if (!hasSelectedRatesTrialTypes) return false;
  return nextRatesTrialType
    ? ratesCurrentTypeValid
    : ratesAllSelectedTypesValid;
};

/**
 * On the Rates & Dosage step, the primary button advances to the next
 * sub-tab (Seeds -> CP & Biologicals -> Fertiliser) before it ever submits
 * the form, so its label needs to reflect the next sub-tab, not the next step.
 */
export const getPrimaryNextLabel = (
  current: number,
  nextRatesTrialType: RatesTrialType | undefined,
  t: any,
  formatTranslationFn: any,
  steps: any
): string => {
  if (current === 2 && nextRatesTrialType) {
    return RATES_TRIAL_LABELS[nextRatesTrialType];
  }

  return getNextButtonLabel(current, t, formatTranslationFn, steps);
};

/**
 * ab_line entries carry a UI-only `selectedForStrips` flag that the trial
 * update API must never receive.
 */
export const cleanAbLines = (abLines: any[] | undefined): any[] | undefined =>
  abLines?.map((abLine: any) => {
    const { selectedForStrips, ...cleanAbLine } = abLine;
    return cleanAbLine;
  });

/**
 * Builds the `standard_field_properties` payload sent alongside a Rates &
 * Dosage / Summary step update — identical shape for both steps.
 */
export const buildStandardFieldPropertiesPayload = (ratesDosage: any): any => ({
  standard_product: ratesDosage.standard_product,
  standard_seed_rate: ratesDosage.standard_seed_rate,
  standard_seed_product_id: ratesDosage.standard_seed_product_id,
  standard_fertilisers: ratesDosage.standard_fertilisers,
  standard_biological_application_settings: Array.isArray(
    ratesDosage?.standard_biological_application_settings
  )
    ? ratesDosage.standard_biological_application_settings.filter(Boolean)
    : [],
  standard_tank_mix_rates: ratesDosage?.standard_tank_mix_rates ?? [],
});

/**
 * When strips mode is on, folds the optimized strips layout into the trial
 * update payload. Falls back to the payload untouched (surfacing a toast +
 * console error) if optimization fails, so a strip-data bug never blocks the
 * rest of the save.
 */
export const mergeStripsDataIntoPayload = (
  payload: any,
  stripsData: NewTrialStateInterface["stripsData"] | undefined,
  t: any,
  errorMessageKey: string
): any => {
  if (!stripsData?.isStripMode) return payload;

  try {
    const optimizedStripsData = optimizeStripsDataForBackend(
      stripsData as FullStripsData
    );
    const backendStripsFormat = transformToBackendFormat(optimizedStripsData);

    return { ...payload, ...backendStripsFormat };
  } catch (err) {
    toast.error(formatTranslation(t("common.error.update")));
    console.error(formatTranslation(t(errorMessageKey)), err);
    return payload;
  }
};

/**
 * Maps a rates-trial sub-tab to the property key it occupies on a
 * `trial_plots[].properties` entry.
 */
export const RATES_TRIAL_PLOT_PROPERTY_KEY: Record<
  RatesTrialType,
  "seeds" | "biologicals" | "fertilisers"
> = {
  seed: "seeds",
  biological: "biologicals",
  nutrient: "fertilisers",
};

/**
 * Whether a single plot's seeds/biologicals/fertilisers section has no
 * meaningful data entered yet (as opposed to merely being present with
 * zeroed-out defaults).
 */
export const isRatesDosagePlotSectionEmpty = (
  section: any,
  propertyKey: "seeds" | "biologicals" | "fertilisers"
): boolean => {
  if (!section) return true;

  if (propertyKey === "biologicals") {
    const treatments: any[] = Array.isArray(section.treatments)
      ? section.treatments.flat()
      : [];
    return !treatments.some(
      (treatment: any) =>
        Boolean(treatment?.treated) ||
        (Array.isArray(treatment?.selected_treatment) &&
          treatment.selected_treatment.length > 0)
    );
  }

  const entries: any[] = Array.isArray(section.rates_and_dosages)
    ? section.rates_and_dosages
    : [];

  if (propertyKey === "seeds") {
    return !entries.some(
      (entry: any) =>
        Number(entry?.rate) > 0 || Boolean(entry?.variety?.trim?.())
    );
  }

  // fertilisers (nutrient)
  return !entries.some(
    (entry: any) => Number(entry?.rate) > 0 || Number(entry?.amount) > 0
  );
};

/**
 * Of the given candidate rates-trial types, returns only those that have
 * actual (non-empty) data on at least one of the given trial_plots.
 */
export const getRatesTrialTypesWithData = (
  trialPlots: any[] | undefined,
  candidateTypes: RatesTrialType[]
): RatesTrialType[] => {
  if (!Array.isArray(trialPlots)) return [];

  return candidateTypes.filter((type) => {
    const propertyKey = RATES_TRIAL_PLOT_PROPERTY_KEY[type];
    return trialPlots.some(
      (plot) =>
        !isRatesDosagePlotSectionEmpty(
          plot?.properties?.[propertyKey],
          propertyKey
        )
    );
  });
};

/**
 * The rates-trial types relevant at this point in the Rates & Dosage step:
 * the protocol's selected types (e.g. a protocol might only offer seed +
 * nutrient, skipping biological entirely), capped to those up to and
 * including `nextRatesTrialType` for an intermediate sub-tab save. Omitting
 * `nextRatesTrialType` (the step's final save) returns every selected type.
 *
 * The cutoff is applied to the *selected* list, not the raw canonical
 * order — otherwise a protocol that skips a type (seed + nutrient, no
 * biological) would incorrectly pull that skipped type back in merely
 * because it sits between seed and nutrient positionally.
 */
export const getCandidateRatesTrialTypes = (
  protocolTypes: string[] | undefined,
  nextRatesTrialType?: RatesTrialType
): RatesTrialType[] => {
  const selectedTypes = RATES_TRIAL_ORDER.filter((type) =>
    protocolTypes?.includes(type)
  );
  if (!nextRatesTrialType) return selectedTypes;

  const cutoffIndex = selectedTypes.indexOf(nextRatesTrialType);
  return cutoffIndex === -1
    ? selectedTypes
    : selectedTypes.slice(0, cutoffIndex + 1);
};

/**
 * When saving an intermediate Rates & Dosage sub-tab (Seeds -> CP &
 * Biologicals -> Fertiliser), the trial update API rejects sections that
 * haven't been filled in yet. This keeps, per plot, only the sections up to
 * and including `nextRatesTrialType` (the sub-tab about to be entered), and
 * additionally drops any of those sections if every plot's data for it is
 * still empty (e.g. the user never entered anything while passing through
 * that tab). Returns `trialPlots` unchanged when there's no next sub-tab
 * (i.e. this is the final save for the step, not an intermediate one).
 */
export const filterRatesDosagePlotPropertiesForSubStep = (
  trialPlots: any[] | undefined,
  nextRatesTrialType: RatesTrialType | undefined,
  protocolTypes: string[] | undefined
): any[] | undefined => {
  if (!nextRatesTrialType || !Array.isArray(trialPlots)) return trialPlots;

  const candidateTypes = getCandidateRatesTrialTypes(
    protocolTypes,
    nextRatesTrialType
  );
  const typesWithData = getRatesTrialTypesWithData(trialPlots, candidateTypes);
  const allowedKeys = typesWithData.map(
    (type) => RATES_TRIAL_PLOT_PROPERTY_KEY[type]
  );

  return trialPlots.map((plot) => {
    const properties = plot?.properties ?? {};
    const filteredProperties: any = {};

    Object.keys(properties).forEach((key) => {
      if (!allowedKeys.includes(key as (typeof allowedKeys)[number])) return;
      filteredProperties[key] = properties[key];
    });

    return { ...plot, properties: filteredProperties };
  });
};
